import { Dtos, TagId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminEntityTagsQueryKey } from "../entity.keys";

export const useAdminCreateEntityTag = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		mutationFn: async (newEntityTag: Dtos.Tag.Create.Request) =>
			await post<Dtos.Tag.Get.Response>("/v2/admin/tags", newEntityTag),
		onMutate: async (newEntityTag) => {
			await queryClient.cancelQueries({ queryKey: adminEntityTagsQueryKey() });
			const previousEntityTags = queryClient.getQueryData<Dtos.Tag.GetAll.Response>(
				adminEntityTagsQueryKey(),
			);
			queryClient.setQueryData<Dtos.Tag.GetAll.Response>(adminEntityTagsQueryKey(), (old) => {
				const tempId = ((old?.length ?? 0) + 1).toString() as TagId;
				if (old === undefined) {
					return [{ id: tempId, ...newEntityTag }];
				}

				return [...old, { id: tempId, ...newEntityTag }].sort((a, b) =>
					a.displayName.localeCompare(b.displayName),
				);
			});

			return { previousEntityTags };
		},
		onError: (_err, _newEntityTag, context) => {
			queryClient.setQueryData(adminEntityTagsQueryKey(), context?.previousEntityTags);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: adminEntityTagsQueryKey() });
		},
	});

	return {
		createEntityTag: mutation.mutate,
		createEntityTagLoading: mutation.isPending,
		createEntityTagError: mutation.error,
		dismissCreateEntityTagError: () => {
			mutation.reset();
		},
	};
};
